import PropTypes from 'prop-types'
import React from 'react'
import cx from 'classnames'

import listClass from 'components/BetterList/List.scss'
import Button from 'components/Button'
import List from 'components/BetterList'

import { compareParseInt } from 'helpers/index'
import { createNewPath } from 'helpers/components'
import moment from 'helpers/moment'

import MdEdit from 'react-icons/lib/md/edit'
import MdDelete from 'react-icons/lib/md/delete'

export class PricelistList extends React.Component {
  getBaseCollection = () => {
    return this.props.pricelist.filter(product => product &&
      (this.props.isAdmin || product.enabled) && // admin can see sold out
      (!product.category || !product.category.hidden) // hide hidden categories
    )
  };

  // this will update or create a daily_product with enabled=false
  handleDisable = pricelistProduct => {
    if (!this.props.editing) return false

    let data = Object.assign({}, pricelistProduct, {
      date: moment(this.props.date).format(), // force to current date
      enabled: false
    })

    if (data.daily_product_id) {
      data.id = data.daily_product_id
      delete data.daily_product_id
    }

    if (data.id) return this.props.put(data, { redirect: false }) // already exists, just update it
    return this.props.post(data, { redirect: false }) // doesn't exist, create a new daily_product
  };

  sortList = (a, b) => {
    // !enabled at bottom, then sort by type then name
    if (a.enabled > b.enabled) return -1
    if (a.enabled < b.enabled) return 1
    return a.type && a.type.name && b.type && b.type.name && a.type.name.localeCompare(b.type.name) ||
      a.name && b.name && a.name.localeCompare(b.name) || 0
  };

  getRowLink = pricelistProduct => {
    if (this.props.editing && pricelistProduct.daily_product_id) {
      return createNewPath(this.props.date, 'daily_products', this.props, {
        id: pricelistProduct.daily_product_id,
        action: 'edit'
      })
    }

    return createNewPath(
      this.props.date,
      this.props.editing ? 'daily_products' : 'orders', // if we're editing, new pricelist, not editing, new order
      this.props,
      {
        append: `?product_id=${pricelistProduct.product_id}`,
        action: 'new'
      }
    )
  };

  getRowOptions = pricelistProduct => {
    let array = []
    if (!pricelistProduct.enabled) array.push('disabled')
    if (this.props.editing) array.push('alwaysLink')

    return array
  };

  getRowColumns = (pricelistProduct, index, array) => {
    let columns = [
      [
        <span className={listClass['column--bold']}>{pricelistProduct.name}</span>,
        pricelistProduct.scientific_name && <span className={cx(listClass['column--italic'], listClass['column--unimportant'])}>
          {pricelistProduct.scientific_name}
        </span> || null,
        pricelistProduct.details && <span className={listClass['column--unimportant']}>
          {pricelistProduct.details}
        </span> || null
      ],
      pricelistProduct.tasting_notes && [
        pricelistProduct.sourcing_location,
        pricelistProduct.sourcing_method && <i>{pricelistProduct.sourcing_method}</i>,
        pricelistProduct.tasting_notes && pricelistProduct.tasting_notes || pricelistProduct.fishing_vessel
      ] || [
        pricelistProduct.sourcing_location,
        pricelistProduct.fishing_vessel && <i>{pricelistProduct.fishing_vessel}</i>,
        pricelistProduct.sourcing_method
      ],
      pricelistProduct.prices && pricelistProduct.prices.filter(o => o.price).map(o => {
        if (!o.option) o.option = this.props.options.find(option => compareParseInt(option.id, o.option_id))

        return o.option && o.option.name || ''
      }),
      !pricelistProduct.enabled && 'sold out' ||
        pricelistProduct.prices && pricelistProduct.prices.filter(o => o.price).map(o => {
          if (!o.price || !Number(o.price)) return null
          if (!o.uom) o.uom = this.props.uoms.find(uom => compareParseInt(uom.id, o.uom_id))
          let uom = o.uom && (o.uom.shorthand || o.uom.name) || ''

          return <span className={listClass['column--nowrap']}>
            <strong>{Number(o.price).formatPrice()}</strong>{uom && ` /${uom}`}
          </span>
        })
    ]

    if (this.props.editing) {
      columns.push([
        <Button element='a' options={['green', 'small']} href={this.getRowLink(pricelistProduct)}><MdEdit /></Button>,
        <Button options={['red', 'small']} callback={() => this.handleDisable(pricelistProduct)}><MdDelete /></Button>
      ])
    }

    return columns
  };

  render () {
    const columnOptions = [
      null,
      ['italic', 'center', 'desktop'],
      ['center', 'nowrap'],
      ['bold', 'right']
    ]

    const headers = [ 'product', 'information', 'spec', 'price' ]
    if (this.props.editing) headers.push('')

    return (
      <List
        {...this.props}

        pristineCollection={this.props.pricelist} // so we can access sold out stuff
        collection={this.getBaseCollection()}
        columnOptions={columnOptions}
        headers={headers}

        getRowLink={this.props.getRowLink || this.getRowLink}
        getRowOptions={this.props.getRowOptions || this.getRowOptions}
        getRowProps={this.props.getRowProps}
        getRowColumns={this.props.getRowColumns || this.getRowColumns}
        sort={this.props.sortList || this.sortList}

        rowUniqueIdentifier={'product_id'}
        groupBy={'category_id'}
        groupTitle={'category.name'}
        groupDesc={'category.desc'}
        sortGroup={'category.priority'}
        filter={[
          'name', 'scientific_name', 'details',
          'tasting_notes', 'fishing_vessel', 'sourcing_method', 'sourcing_location',
          'type.name', 'category.name'
        ]}
        filterPlaceholder='search pricelist'

        partial={this.props.partial}
      >
        {this.props.children}
      </List>
    )
  }
}

PricelistList.propTypes = {
  options: PropTypes.array.isRequired,
  uoms: PropTypes.array.isRequired,
  pricelist: PropTypes.array.isRequired,

  partial: PropTypes.bool,
  editing: PropTypes.bool,
  params: PropTypes.object,
  date: PropTypes.any,
  baseUrl: PropTypes.string,
  isAdmin: PropTypes.bool,

  // actions
  put: PropTypes.func,
  post: PropTypes.func,

  location: PropTypes.object,
  dispatch: PropTypes.func,

  // component props
  sortList: PropTypes.func,
  getRowLink: PropTypes.func,
  getRowOptions: PropTypes.func,
  getRowColumns: PropTypes.func,
  getRowProps: PropTypes.func,

  children: PropTypes.node
}

PricelistList.defaultProps = {
  options: [],
  pricelist: [],
  partial: false,
  editing: false
}

export default PricelistList



// WEBPACK FOOTER //
// ./src/components/Pricelist/List/List.js